import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DatasheetWarningBanner } from "@/components/datasheets/DatasheetWarningBanner";
import { NewDatasheetOrderDialog } from "@/components/datasheets/NewDatasheetOrderDialog";
import { RecordPurchaseDialog } from "@/components/datasheets/RecordPurchaseDialog";
import { AdjustBalanceDialog } from "@/components/datasheets/AdjustBalanceDialog";
import { IssueStatementDialog } from "@/components/datasheets/IssueStatementDialog";
import { useConfirm } from "@/hooks/use-confirm";
import { usePermissions } from "@/hooks/use-permissions";
import { toast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import { modeLabel, useDatasheet, useDeleteDatasheet } from "@/lib/datasheets";
import { OverviewSection } from "./sections/OverviewSection";
import { SizesSection } from "./sections/SizesSection";
import { PurchasesSection } from "./sections/PurchasesSection";
import { OrdersSection } from "./sections/OrdersSection";
import { AuthContactsSection } from "./sections/AuthContactsSection";
import { StatementSection } from "./sections/StatementSection";

export function DatasheetDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const datasheetId = Number(id ?? 0);
  const { data: sheet, isLoading } = useDatasheet(datasheetId);
  const { has } = usePermissions();
  const [orderOpen, setOrderOpen] = useState(false);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const confirm = useConfirm();
  const del = useDeleteDatasheet(datasheetId);

  async function handleDelete() {
    if (!sheet) return;
    const ok = await confirm({
      title: "Delete datasheet?",
      body: sheet.companyName ?? `Datasheet #${sheet.datasheetId}`,
      confirmLabel: "Delete",
      intent: "destructive",
    });
    if (!ok) return;
    try {
      await del.mutateAsync(sheet.rowVersionBase64);
      toast.success("Datasheet deleted");
      void navigate("/datasheets");
    } catch (e) {
      const detail = e instanceof ApiError ? (e.body as { detail?: string } | null)?.detail : null;
      toast.error(detail ?? "Could not delete the datasheet");
    }
  }

  if (isLoading) return <p className="p-6 text-sm text-muted-foreground">Loading…</p>;
  if (!sheet) return <p className="p-6 text-sm text-muted-foreground">Datasheet not found.</p>;

  const totalRemaining = sheet.sizes.reduce((sum, s) => sum + s.quantityAvail, 0);

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold">{sheet.companyName ?? `Datasheet #${sheet.datasheetId}`}</h1>
          <Badge variant="secondary">{modeLabel(sheet.mode)}</Badge>
          {has("datasheet:order") ? (
            <Button size="sm" onClick={() => setOrderOpen(true)}>New order</Button>
          ) : null}
          {has("datasheet:order") ? (
            <Button size="sm" variant="outline" onClick={() => void navigate(`/datasheets/${datasheetId}/orders/build`)}>Build from badges</Button>
          ) : null}
          {has("datasheet:write") ? (
            <Button size="sm" variant="outline" onClick={() => setPurchaseOpen(true)}>Record purchase</Button>
          ) : null}
          {has("datasheet:adjust") ? (
            <Button size="sm" variant="outline" onClick={() => setAdjustOpen(true)}>Adjust balance</Button>
          ) : null}
          {has("datasheet:write") ? (
            <Button size="sm" variant="outline" onClick={() => setIssueOpen(true)}>Issue statement</Button>
          ) : null}
          {has("datasheet:delete") ? (
            <Button size="sm" variant="destructive" onClick={() => void handleDelete()}>Delete</Button>
          ) : null}
        </div>
        <p className="text-sm text-muted-foreground">
          {sheet.datasheetTypeDescription ?? sheet.datasheetTypeCode}
          {sheet.primaryContactName ? ` · ${sheet.primaryContactName}` : ""}
          {sheet.tracksQuantity ? ` · ${totalRemaining} remaining` : ""}
        </p>
      </header>

      <DatasheetWarningBanner warnings={sheet.warnings} />

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="sizes">Sizes &amp; rates</TabsTrigger>
          <TabsTrigger value="orders">Orders</TabsTrigger>
          <TabsTrigger value="purchases">Purchases</TabsTrigger>
          <TabsTrigger value="contacts">Authorised contacts</TabsTrigger>
          <TabsTrigger value="statement">Statement</TabsTrigger>
        </TabsList>

        <TabsContent value="overview"><OverviewSection sheet={sheet} /></TabsContent>
        <TabsContent value="sizes"><SizesSection sheet={sheet} /></TabsContent>
        <TabsContent value="orders"><OrdersSection datasheetId={datasheetId} /></TabsContent>
        <TabsContent value="purchases"><PurchasesSection datasheetId={datasheetId} /></TabsContent>
        <TabsContent value="contacts"><AuthContactsSection datasheetId={datasheetId} /></TabsContent>
        <TabsContent value="statement"><StatementSection datasheetId={datasheetId} /></TabsContent>
      </Tabs>

      <NewDatasheetOrderDialog
        open={orderOpen}
        onOpenChange={setOrderOpen}
        datasheetId={datasheetId}
        sizes={sheet.sizes}
        pricesPerOrder={sheet.pricesPerOrder}
      />
      <RecordPurchaseDialog open={purchaseOpen} onOpenChange={setPurchaseOpen} datasheetId={datasheetId} sizes={sheet.sizes} />
      <AdjustBalanceDialog open={adjustOpen} onOpenChange={setAdjustOpen} datasheetId={datasheetId} sizes={sheet.sizes} />
      <IssueStatementDialog open={issueOpen} onOpenChange={setIssueOpen} datasheetId={datasheetId} />
    </div>
  );
}
